import { useState } from 'react';

import { useToastStore } from '../../../stores/toastStore';
import type { DiResourceResponse } from '../api';
import { DiResourceForm } from '../components/DiResourceForm';
import { PlanBadge } from '../components/PlanBadge';
import {
  useCreateDiResource,
  useDeleteDiResource,
  useDiResources,
  useTestDiResource,
  useUpdateDiResource,
} from '../hooks/useDiResources';

export function DiResourcesPage(): JSX.Element {
  const { data: resources, isLoading, isError } = useDiResources();
  const pushToast = useToastStore((s) => s.push);

  const createMutation = useCreateDiResource();
  const updateMutation = useUpdateDiResource();
  const deleteMutation = useDeleteDiResource();
  const testMutation = useTestDiResource();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<DiResourceResponse | null>(null);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Recursos Document Intelligence</h1>
          <p className="mt-1 text-sm text-slate-500">Endpoints de Azure DI asignados por plan de licencia.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>Añadir recurso</button>
      </header>

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudo cargar la lista.</div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Endpoint</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Planes</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-slate-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 animate-pulse rounded bg-slate-100" /></td>
                  ))}
                </tr>
              ))
            ) : (
              (resources ?? []).map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.displayName}</td>
                  <td className="max-w-xs px-4 py-3">
                    <span className="block truncate font-mono text-xs text-slate-600">{r.endpoint}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.assignedLicenses.map((l) => <PlanBadge key={l} plan={l} />)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${r.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {r.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className="text-xs text-slate-600 hover:text-primary disabled:opacity-50"
                        disabled={testMutation.isPending}
                        onClick={() => {
                          testMutation.mutate(r.id, {
                            onSuccess: (res) => {
                              const tone = res.success ? 'success' : 'error';
                              const title = res.success ? `Test OK · ${res.latencyMs} ms` : 'Test fallido';
                              pushToast(res.success || !res.errorMessage
                                ? { tone, title }
                                : { tone, title, description: res.errorMessage });
                            },
                            onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
                          });
                        }}
                      >
                        {testMutation.isPending && testMutation.variables === r.id ? 'Probando…' : 'Test'}
                      </button>
                      <button
                        type="button"
                        className="text-xs text-slate-600 hover:text-primary"
                        onClick={() => setEditingResource(r)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="text-xs text-rose-600 hover:text-rose-800"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (!confirm(`¿Eliminar "${r.displayName}"?`)) return;
                          deleteMutation.mutate(r.id, {
                            onSuccess: () => pushToast({ tone: 'success', title: 'Recurso eliminado' }),
                            onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
                          });
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <DiResourceForm
          mode="create"
          busy={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
          onCreate={(req) => {
            createMutation.mutate(req, {
              onSuccess: () => { pushToast({ tone: 'success', title: 'Recurso creado' }); setCreateOpen(false); },
              onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
            });
          }}
        />
      ) : null}

      {editingResource !== null ? (
        <DiResourceForm
          mode="edit"
          resource={editingResource}
          busy={updateMutation.isPending}
          onClose={() => setEditingResource(null)}
          onUpdate={(req) => {
            updateMutation.mutate(
              { id: editingResource.id, request: req },
              {
                onSuccess: () => { pushToast({ tone: 'success', title: 'Recurso actualizado' }); setEditingResource(null); },
                onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}
